import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaPhone,
  FaEnvelope,
  FaInstagram,
  FaFacebookF,
  FaMapMarkerAlt,
  FaPaperPlane,
} from 'react-icons/fa';
import './Contact.css';

const Contact = ({ division = 'software' }) => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build mailto link
    const subject = encodeURIComponent(`Website Inquiry - ${formData.service || 'General'}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nService: ${formData.service}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:kat99pvtltd@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      label: 'Call Us',
      values: ['+94 76 120 2374', '+94 77 667 0284', '+94 77 235 0923'],
      href: 'tel:+94761202374',
    },
    {
      icon: <FaEnvelope />,
      label: 'Email',
      values: ['kat99pvtltd@gmail.com'],
      href: 'mailto:kat99pvtltd@gmail.com',
    },
    {
      icon: <FaInstagram />,
      label: 'Instagram',
      values: division === 'software' ? ['@kat99_pvt_ltd'] : ['@kat99_pvt_ltd', '@kat99_constructions'],
      href: 'https://instagram.com/kat99_pvt_ltd',
    },
    {
      icon: <FaFacebookF />,
      label: 'Facebook',
      values: division === 'software' ? ['KAT99 Pvt Ltd'] : ['KAT99 Pvt Ltd', 'Constructions by KAT99 | Galle'],
      href: 'https://www.facebook.com/profile.php?id=61573995033166',
    },
    {
      icon: <FaMapMarkerAlt />,
      label: 'Location',
      values: ['Sri Lanka'],
      href: null,
    },
  ];

  return (
    <section className="contact" id="contact" ref={ref}>
      <div className="contact__container">
        <motion.div
          className="contact__header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="section-tag">Get in Touch</span>
          <h2 className="section-title">
            Let's Build Something
            <br />
            <span className="text-green">Amazing Together</span>
          </h2>
          <p className="section-description">
            {division === 'software'
              ? 'Have a project in mind? We\'d love to hear about it. Fill in the form and we\'ll get back to you soon.'
              : 'Looking to build or renovate? Tell us about your project and we\'ll get back to you.'}
          </p>
        </motion.div>

        <div className="contact__content">
          <motion.div
            className="contact__info"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {contactInfo.map((item, i) => (
              <a
                key={i}
                className="contact__info-card"
                href={item.href}
                target={item.href?.startsWith('http') ? '_blank' : undefined}
                rel={item.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                <div className="contact__info-icon">{item.icon}</div>
                <div>
                  <span className="contact__info-label">{item.label}</span>
                  {item.values.map((v, j) => (
                    <span key={j} className="contact__info-value">{v}</span>
                  ))}
                </div>
              </a>
            ))}

            <div className="contact__map-placeholder">
              <FaMapMarkerAlt className="contact__map-icon" />
              <span>Based in Sri Lanka</span>
              <span className="contact__map-sub">Serving clients nationwide</span>
            </div>
          </motion.div>

          <motion.form
            className="contact__form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="contact__form-group">
              <label htmlFor="name">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="contact__form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="contact__form-group">
              <label htmlFor="service">Service Needed</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              >
                <option value="">Select a service</option>
                {division === 'software' ? (
                  <optgroup label="Software Division">
                    <option value="Mobile App Development">Mobile App Development</option>
                    <option value="Web Application">Web Application</option>
                    <option value="POS System">POS System</option>
                    <option value="Loyalty & Reward System">Loyalty & Reward System</option>
                    <option value="E-Commerce">E-Commerce Solution</option>
                    <option value="Custom Software">Custom Software</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="Cybersecurity">Cybersecurity & Pentesting</option>
                    <option value="Other">Other</option>
                  </optgroup>
                ) : (
                  <optgroup label="Construction Division">
                    <option value="Full Construction">Full Construction</option>
                    <option value="Renovation">Renovation</option>
                    <option value="Woodwork">Custom Woodwork</option>
                    <option value="Interior Design">Interior Design</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Other">Other</option>
                  </optgroup>
                )}
              </select>
            </div>
            <div className="contact__form-group">
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Tell us about your project..."
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>
            <motion.button
              type="submit"
              className={`contact__form-btn ${submitted ? 'contact__form-btn--sent' : ''}`}
              whileTap={{ scale: 0.97 }}
            >
              <FaPaperPlane />
              {submitted ? 'Message Sent!' : 'Send Message'}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
